import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  Box, // Added Box for loading indicator centering
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { Timestamp } from "firebase/firestore"; // Import Timestamp
import useFirestore from "../../../../config/fireBaseHook"; // Import useFirestore
import useSendSchoolNotification from "../../../../config/sendSchoolNotificationHook"; // Import notification hook
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";

const ChangeCourseRequest = ({ schoolId, pathwayEmail, opEmail }) => {
  // Use Firestore hook to add data to 'ChangeCourseRequest' collection
  const { addData: addFirestoreData } = useFirestore("ChangeCourseRequest"); // Collection name as per request
  // Use notification hook
  const {
    sendNotification,
    loading: notificationLoading, // Renamed to avoid conflict if needed, though we'll use a single 'loading' state
    error: notificationError,
  } = useSendSchoolNotification();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false); // Combined loading state for form submission
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // 'success' or 'error'

  const initialFormData = {
    sectionName: "",
    changeAction: "",
    startDate: "",
    endDate: "",
    modality: "",
    instructorType: "",
    instructorName: "",
    startTime: "",
    endTime: "",
    daysOfWeek: "",
    schoolName: "",
    fromName: "",
    notes: "", // Added notes field
    schoolId: schoolId || "",
    pathwayEmail: pathwayEmail || "",
    opEmail: opEmail || "",
    // Date_Submitted will be added dynamically on submit
  };

  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      schoolId: schoolId || "",
      pathwayEmail: pathwayEmail || "",
      opEmail: opEmail || "",
    }));
  }, [schoolId, pathwayEmail, opEmail]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.instructorType) {
      setSnackbarMessage("Instructor Type is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    if (!formData.sectionName) {
      setSnackbarMessage("Section Name is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    if (!formData.changeAction) {
      setSnackbarMessage("Change Action is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    // Add more validation as needed

    setLoading(true);
    setSnackbarMessage(""); // Clear previous messages

    const emailBodyContent = [
      `<strong>School Name:</strong> ${formData.schoolName || "N/A"}`,
      `<strong>Submitter Name:</strong> ${formData.fromName || "N/A"}`,
      `<strong>Section Name:</strong> ${formData.sectionName || "N/A"}`,
      `<strong>Change Action:</strong> ${formData.changeAction || "N/A"}`,
      `<strong>Start Date:</strong> ${formData.startDate || "N/A"}`,
      `<strong>End Date:</strong> ${formData.endDate || "N/A"}`,
      `<strong>Modality:</strong> ${formData.modality || "N/A"}`,
      `<strong>Instructor Type:</strong> ${formData.instructorType || "N/A"}`,
      `<strong>Instructor Name:</strong> ${formData.instructorName || "N/A"}`,
      `<strong>Start Time:</strong> ${formData.startTime || "N/A"}`,
      `<strong>End Time:</strong> ${formData.endTime || "N/A"}`,
      `<strong>Days of Week:</strong> ${formData.daysOfWeek || "N/A"}`,
      `<strong>Notes:</strong> ${formData.notes || "N/A"}`,
    ].join("<br />");

    const dataForFirestore = {
      ...formData,
      Date_Submitted: Timestamp.now(),
    };

    // Prepare data for the notification, similar to NewCourseRequest
    const dataForNotification = {
      // Essential routing and identification information
      pathwayEmail: formData.pathwayEmail,
      opEmail: formData.opEmail,
      schoolId: formData.schoolId,
      fromName: formData.fromName, // For sender identification or 'From' field
      schoolName: formData.schoolName, // For context
      emailBodyContent: emailBodyContent,
    };

    try {
      await addFirestoreData(dataForFirestore);
      await sendNotification(
        dataForNotification,
        "Change Course Request",
        "ChangeCourseRequest"
      );
      setSnackbarMessage("Request submitted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      handleClose();
    } catch (error) {
      console.error("Error submitting form:", error);
      const message = error.message || "An error occurred during submission.";
      setSnackbarMessage(`Error: ${message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setLoading(false);
    setFormData({
      ...initialFormData,
      schoolId: schoolId || "",
      pathwayEmail: pathwayEmail || "",
      opEmail: opEmail || "",
    });
  };

  return (
    <div>
      <Button
        variant="contained"
        color="primary"
        startIcon={<FontAwesomeIcon icon={faPlus} />}
        onClick={handleClickOpen}
        sx={{ color: "white" }}
      >
        Change Course Request
      </Button>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <ModalDialogTitle onClose={handleClose}>
          Change Course Request
        </ModalDialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Section"
                    name="sectionName"
                    fullWidth
                    required
                    value={formData.sectionName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth margin="dense">
                    <InputLabel>Change Action</InputLabel>
                    <Select
                      name="changeAction"
                      value={formData.changeAction}
                      onChange={handleChange}
                      label="Change Action" // Add label for accessibility
                      required
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      <MenuItem value="Cancel">Cancel</MenuItem>
                      <MenuItem value="Change">Change</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Start Date"
                    type="date"
                    name="startDate"
                    fullWidth
                    value={formData.startDate}
                    onChange={handleChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="End Date"
                    type="date"
                    name="endDate"
                    fullWidth
                    value={formData.endDate}
                    onChange={handleChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth margin="dense">
                    <InputLabel>Modality</InputLabel>
                    <Select
                      name="modality"
                      value={formData.modality}
                      onChange={handleChange}
                      label="Modality" // Add label for accessibility
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      <MenuItem value="Face to Face">Face to Face</MenuItem>
                      <MenuItem value="Online">Online</MenuItem>
                      <MenuItem value="Remote">Remote</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth margin="dense" required>
                    <InputLabel>Instructor Type</InputLabel>
                    <Select
                      name="instructorType"
                      value={formData.instructorType}
                      onChange={handleChange}
                      label="Instructor Type" // Add label for accessibility
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      <MenuItem value="Sinclair">Sinclair</MenuItem>
                      <MenuItem value="High School">High School</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Instructor Name"
                    name="instructorName"
                    fullWidth
                    value={formData.instructorName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Start Time"
                    name="startTime"
                    type="time"
                    fullWidth
                    value={formData.startTime}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="End Time"
                    name="endTime"
                    type="time"
                    fullWidth
                    value={formData.endTime}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Days of the Week (e.g., MWF, TR)"
                    name="daysOfWeek"
                    fullWidth
                    value={formData.daysOfWeek}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="School Name"
                    name="schoolName"
                    fullWidth
                    value={formData.schoolName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Your Name (Submitter)"
                    name="fromName"
                    fullWidth
                    value={formData.fromName}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    margin="dense"
                    label="Notes"
                    name="notes"
                    fullWidth
                    multiline
                    rows={3}
                    value={formData.notes}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </form>
          )}
        </DialogContent>
        <ModalActions
          onSave={handleSubmit}
          saving={loading || notificationLoading}
          saveLabel="Submit"
        />
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ChangeCourseRequest;
